import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables');
}

// Track if we're currently offline to prevent repeated error logs
let isOfflineMode = false;

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Reduce retry attempts when offline to prevent console spam
    storageKey: 'creators-archive-auth',
    // Email confirmation redirect URL
    flowType: 'pkce',
  },
  global: {
    // Add custom fetch wrapper to handle network errors gracefully
    fetch: async (url, options = {}) => {
      try {
        const response = await fetch(url, options);
        
        // If we were offline and now got a response, we're back online
        if (isOfflineMode) {
          isOfflineMode = false;
          console.log('🌐 Supabase: Connection restored');
        }
        
        return response;
      } catch (error: any) {
        // Check if it's a network error
        const isNetworkError = 
          error.message?.includes('Failed to fetch') ||
          error.message?.includes('ERR_INTERNET_DISCONNECTED') ||
          error.message?.includes('NetworkError') ||
          !navigator.onLine;

        if (isNetworkError) {
          // Only log once when going offline
          if (!isOfflineMode) {
            isOfflineMode = true;
            console.warn('📡 Supabase: Network disconnected, operating in offline mode');
          }
          
          // Return a mock response to prevent errors from propagating
          // This allows the app to continue functioning with cached data
          return new Response(
            JSON.stringify({ 
              error: { 
                message: 'No internet connection',
                status: 0,
                offline: true 
              } 
            }),
            { 
              status: 503,
              statusText: 'Service Unavailable',
              headers: { 'Content-Type': 'application/json' }
            }
          );
        }
        
        // Re-throw non-network errors
        throw error;
      }
    },
  },
});
